/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import isNumber from 'lodash/isNumber';
import isBoolean from 'lodash/isBoolean';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';
import isNil from 'lodash/isNil';
import Subtract from '@carbon/icons-react/lib/subtract/20';
import difference from 'lodash/difference';

export const DEVICE_INFO_KEYS = [
  'description',
  'descriptiveLocation',
  'serialNumber',
  'deviceClass',
  'fwVersion',
  'hwVersion',
  'manufacturer',
  'model',
];

export const DATA_TYPES = {
  STRING: 'String',
  NUMBER: 'Number',
  BOOLEAN: 'Boolean',
  OBJECT: 'Object',
  ARRAY: 'Array',
};

export const ROW_ACTION_IDS = {
  REMOVE_PROPERTY: 'REMOVE_PROPERTY',
};

/**
 * deleteRowAction: if set, a delete button will be exposed in an overflow menu for each row. Expected structure:
 *  {
 *    labelText: "delete",
 *  }
 *  when clicked, table's onApplyRowAction callback will be invoked with actionId='REMOVE_PROPERTY' (and the relevant rowId)
 */
export const mapToTableData = (data, readOnly = false, deleteRowAction = undefined) =>
  isNil(data) || !Array.isArray(data)
    ? data
    : [
        ...data
          // filter out any dimensions that have type array/object
          // then map to a format suitable for rendering in a table
          // (the rowId is used the path to the dimension in the Formik data model)
          // .filter(({ type }) => type !== DATA_TYPES.OBJECT && type !== DATA_TYPES.ARRAY)
          // .map((__, i) => ({
          //     id: `[${i}]`,
          //     values: {},
          //     rowActions: undefined
          // })),

          // This reduce replicates the filter/map logic above without losing track of the
          // array index of elements in data
          .reduce(
            (acc, { type }, i) => [
              ...acc,
              ...(type === DATA_TYPES.OBJECT || type === DATA_TYPES.ARRAY
                ? []
                : [
                    {
                      id: `[${i}]`,
                      values: {},
                      rowActions: deleteRowAction
                        ? [
                            {
                              id: ROW_ACTION_IDS.REMOVE_PROPERTY,
                              iconDescription: deleteRowAction.labelText,
                              renderIcon: Subtract,
                              disabled: readOnly,
                            },
                          ]
                        : [],
                    },
                  ]),
            ],
            []
          ),
      ];

export const isDeviceInfoKey = key => DEVICE_INFO_KEYS.some(x => x === key);

export const getValueType = value =>
  isBoolean(value)
    ? DATA_TYPES.BOOLEAN
    : isNumber(value)
    ? DATA_TYPES.NUMBER
    : isPlainObject(value)
    ? DATA_TYPES.OBJECT
    : isArray(value)
    ? DATA_TYPES.ARRAY
    : DATA_TYPES.STRING;

export const getDefaultForType = type => {
  switch (type) {
    case DATA_TYPES.BOOLEAN:
      return false;
    case DATA_TYPES.NUMBER:
      return 0;
    default:
    case DATA_TYPES.STRING:
      return '';
  }
};

/**
 * Maps raw device.deviceInfo and device.metadata fields into a format more suitable for use in our
 * Formik-enabled table view.
 *
 * Type info is included even though this appears redundant (as it is implied by the type of the JSON value)
 * because of how we are dealing with this in formik (it is more like "requested type" used to validate the user-entered value)
 * {
 *  "deviceInfo": { "serialNumber": "xxx" },
 *  "metadata": { "Building": "ABlock", "offset": 22, "enabled": true }
 * }
 * ->
 * [
 *  { "key": "serialNumber", "value": "xxx", "type": "String" },
 *  { "key": "Building", "value": "ABlock", "type": "String"},
 *  { "key": "offset", "value": 22, "type": "Number"},
 *  { "key": "enabled", "value": true, "type": "Boolean"}
 * ]
 *
 *
 * nested values (e.g. object values are ignored - these are not shown in the table view)
 */
export const reduxToFormik = data =>
  !isNil(data)
    ? [
        ...(!isNil(data.deviceInfo)
          ? Object.entries(data.deviceInfo).map(([key, value]) => ({
              key,
              value,
              type: getValueType(value),
            }))
          : []),
        ...(!isNil(data.metadata)
          ? Object.entries(data.metadata).map(([key, value]) => ({
              key,
              value,
              type: getValueType(value),
            }))
          : []),
      ]
    : data;

/**
 * Inverse of reduxToFormik
 */
export const formikToRedux = data =>
  data
    ? // ignores "type" validation will ensure this aligns exactly with the actual type of the JSON value
      data.reduce(
        (acc, { key, value }) =>
          isDeviceInfoKey(key)
            ? {
                deviceInfo: {
                  ...acc.deviceInfo,
                  [key]: value,
                },
                metadata: acc.metadata,
              }
            : {
                metadata: {
                  ...acc.metadata,
                  [key]: value,
                },
                deviceInfo: acc.deviceInfo,
              },
        { deviceInfo: {}, metadata: {} }
      )
    : data;

/**
 * The API handles the deviceInfo field a bit strangely; to remove an existing deviceInfo field
 * instead of omitting it, you have to set its value to the empty string
 *
 * e.g. existing deviceInfo:
 * {"serialNumber": "...", "description": "..."}
 * PUT {"deviceInfo": {}}
 * -> {"serialNumber": "...", "description": "..."} (no effect)
 * PUT {"deviceInfo": {"serialNumber": ""}}
 * -> {description": "..."} (serialNumber field is removed)
 *
 * so, we need to look for fields that are present in the remote device resource (i.e. our redux state)
 * that are not present in our local state (i.e. that passed into this function)
 * (this indicates that the user has removed the field using the metadata editor)
 * and submit them with an empty string value in the request
 *
 * This function takes existingDeviceInfo (i.e. taken from Redux state) and updatedDeviceInfo
 * (i.e. taken from Formik state, includes user's edits),
 * and generates the appropriate deviceInfo subresource that will remove keys on submission
 *
 * existingDeviceInfo: e.g. {"description": "...", "serialNumber": "..."}
 * updatedDeviceInfo: e.g. {"description": "...", "manufacturer": "..."} (removed serialNumber, added manufacturer)
 */
export const buildDeviceInfoUpdate = (existingDeviceInfo, updatedDeviceInfo) => {
  // no deviceInfo yet, no need to remove any keys, just return updatedDeviceInfo unchanged
  if (!existingDeviceInfo) {
    return updatedDeviceInfo;
  }

  if (!isPlainObject(existingDeviceInfo)) {
    throw new Error('existingDeviceInfo must be an object');
  }

  if (!updatedDeviceInfo) {
    throw new Error('updatedDeviceInfo is required');
  }

  if (!isPlainObject(updatedDeviceInfo)) {
    throw new Error('updatedDeviceInfo must be an object');
  }

  const existingDeviceInfoKeys = Object.keys(existingDeviceInfo); // e.g. ['description', 'serialNumber']

  const updatedDeviceInfoKeys = Object.keys(updatedDeviceInfo); // e.g. ['description', 'manufacturer']

  // get any key in currentDeviceInfoKeys that's not also in newDeviceInfoKeys
  // e.g. ['serialNumber']
  const removedDeviceInfoKeys = difference(existingDeviceInfoKeys, updatedDeviceInfoKeys);

  // add them back with empty string values in the resource we submit to the PUT API
  // e.g. {"serialNumber": "", "description": "", "manufacturer": "..."}
  return {
    ...updatedDeviceInfo,
    ...removedDeviceInfoKeys.reduce(
      (acc, removedDeviceInfoKey) => ({ ...acc, [removedDeviceInfoKey]: '' }),
      {}
    ),
  };
};

export default {
  DEVICE_INFO_KEYS,
  DATA_TYPES,
  isDeviceInfoKey,
  getValueType,
  getDefaultForType,
  reduxToFormik,
  formikToRedux,
  mapToTableData,
  buildDeviceInfoUpdate,
};
